import { useTranslation } from 'react-i18next';

import CircleIcon from '@mui/icons-material/Circle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import useTheme from '@mui/material/styles/useTheme';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { ReactComponent as SshKeyIcon } from '~/assets/images/sshkey-icon.svg';

import { SSH_KEYS_TABLE_TEST_IDS } from '../utils/constants';
import { SSHKey } from '../utils/types';

interface SSHKeyRowsProps {
  list?: SSHKey[];
  showStatusColumn?: boolean;
  showTypeColumn?: boolean;
  reAddingKeyId?: number | null;
  onView: (id: number) => void;
  onDelete: (id: number) => void;
  onReAdd?: (id: number) => void;
  onRemove?: (id: number) => void;
  isServerOnline?: boolean;
}

const iconWrapperSx = {
  width: 36,
  height: 36,
  borderRadius: 2,
  bgcolor: 'secondary.light',
  flexShrink: 0,
};

const keyTypeChipSx = {
  height: 26,
  px: 1,
  py: 0.5,
  width: 'fit-content',
  boxSizing: 'border-box',
  borderRadius: 1,
  color: 'primary.main',
  bgcolor: 'secondary.light',
  fontSize: 12,
  fontWeight: 400,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

const activeStatusSx = {
  height: 28,
  width: 'fit-content',
  px: 1.1,
  borderRadius: 3,
  color: 'success.dark',
  bgcolor: 'success.light',
  fontWeight: 600,
};

const notOnServerStatusSx = {
  width: 'fit-content',
  color: 'warning.dark',
  fontWeight: 600,
};

const removeButtonSx = {
  color: 'error.main',
  textTransform: 'none',
  '&:hover': {
    bgcolor: 'action.hover',
  },
};

const SSHKeyRows = ({
  list = [],
  showStatusColumn = false,
  showTypeColumn = true,
  reAddingKeyId = null,
  onView,
  onDelete,
  onReAdd,
  onRemove,
  isServerOnline = true,
}: SSHKeyRowsProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'sshSettings',
  });

  const theme = useTheme();

  const handleReAdd = (id: number) => {
    if (onReAdd) {
      onReAdd(id);
    }
  };

  const handleRemove = (id: number) => {
    if (onRemove) {
      onRemove(id);
    }
  };

  return list.map((row) => {
    const { id, name, alg_type, status, addedAt } = row;

    const isMissingFromServer = status === 'notOnServer';
    const isReAddLoading = reAddingKeyId === id;

    const statusLabel = isMissingFromServer
      ? t('status.notOnServer')
      : t('status.active');

    return (
      <TableRow
        key={id}
        data-testid={SSH_KEYS_TABLE_TEST_IDS.row(id)}
        sx={{
          bgcolor: isMissingFromServer ? 'warning.50' : 'inherit',
        }}
      >
        <TableCell>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={isMissingFromServer ? undefined : iconWrapperSx}
            >
              <SshKeyIcon
                style={{
                  color: isMissingFromServer
                    ? theme.palette.warning.main
                    : theme.palette.primary.main,
                  fontSize: 'small',
                }}
              />
            </Stack>

            <Typography variant="body1" fontWeight={600}>
              {name}
            </Typography>
          </Stack>
        </TableCell>

        {showTypeColumn && (
          <TableCell>
            <Typography variant="body2" sx={keyTypeChipSx}>
              {alg_type}
            </Typography>
          </TableCell>
        )}

        {showStatusColumn && (
          <TableCell>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.8}
              sx={isMissingFromServer ? notOnServerStatusSx : activeStatusSx}
            >
              <CircleIcon
                sx={{
                  width: 7,
                  height: 7,
                  color: isMissingFromServer ? 'warning.main' : 'success.main',
                  flexShrink: 0,
                }}
              />

              <Typography variant="body2">{statusLabel}</Typography>
            </Stack>
          </TableCell>
        )}

        <TableCell>
          <Typography variant="body1">{addedAt}</Typography>
        </TableCell>

        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {isMissingFromServer ? (
            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <LoadingButton
                data-testid={SSH_KEYS_TABLE_TEST_IDS.reAddAction(id)}
                variant="outlined"
                size="small"
                loading={isReAddLoading}
                disabled={!isServerOnline}
                startIcon={<LoopOutlinedIcon fontSize="small" />}
                onClick={() => handleReAdd(id)}
              >
                {t('actions.reAdd')}
              </LoadingButton>

              <Button
                data-testid={SSH_KEYS_TABLE_TEST_IDS.removeAction(id)}
                variant="text"
                color="error"
                size="small"
                sx={removeButtonSx}
                disabled={!isServerOnline}
                onClick={() => handleRemove(id)}
              >
                {t('actions.remove')}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button
                data-testid={SSH_KEYS_TABLE_TEST_IDS.viewAction(id)}
                variant="outlined"
                aria-label={t('actions.view')}
                size="small"
                startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                onClick={() => onView(id)}
              >
                {t('actions.view')}
              </Button>

              <Button
                data-testid={SSH_KEYS_TABLE_TEST_IDS.deleteAction(id)}
                variant="outlined"
                color="error"
                aria-label={t('actions.delete')}
                size="small"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => onDelete(id)}
                disabled={!isServerOnline}
              >
                {t('actions.delete')}
              </Button>
            </Stack>
          )}
        </TableCell>
      </TableRow>
    );
  });
};

export default SSHKeyRows;
